import React, { Fragment, useCallback, useContext, useState } from 'react';
import {
  CurrentStepIndexContext,
  SetCurrentStepIndexContext,
  StepperDataContext
} from '../../../Contexts/StepperContext';
import { useEffect } from 'react';
import Title from '../../../Components/Labels/Title';
import {
  CircularProgress,
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core';
import { CheckPendingRechargeAPI } from '../../../API/PrepaidMeters/PrepaidMetersAPI';
import { PrepaidMeterRechargeStep } from '../PrepaidMeter_enums';
import SweetAlert from '../../../Components/Alerts/SweetAlert';
import MeterInfoCard from '../../../Components/Cards/MeterInfoCard';
import { useForm } from 'react-hook-form';
import BackIcon from '../../../Components/CustomIcons/BackIcon';
import SvgIcMapPointer from '../../../Components/CustomIcons/MapPointerIcon';
import LinkButton from '../../../Components/Buttons/LinkButton';
import { ROUTE_NAMES } from '../../../Routes/Routes';

const CardDataStep = props => {
  const {
    formId,
    setCurrentStep: setVisualStepperIndex,
    setNextButtonText,
    setGoBack,
    setCanSubmit,
    authToken,
    setRequestId
  } = props;
  const classes = useStyles();

  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const currentStepIndex = useContext(CurrentStepIndexContext);
  const stepperData = useContext(StepperDataContext);
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState();
  const [isTransactionPending, setIsTransactionPending] = useState();
  const [isRechargePending, setIsRechargePending] = useState();
  const [transactionData, setTransactionData] = useState();

  const getPendingRecharge = useCallback(
    async meterSerial => {
      const { success, data } = await CheckPendingRechargeAPI(
        authToken,
        meterSerial
      );

      if (!success) {
        setLoading(false);
        return;
      }

      const {
        request,
        isTransactionPending: isDbTransactionPending
      } = data.rechargeInfo;

      setIsTransactionPending(isDbTransactionPending);
      setIsRechargePending(true);
      setLoading(false);

      if (isDbTransactionPending) {
        setCanSubmit(false);
        return;
      }

      setTransactionData(request.data);
      setRequestId(request.id);
    },
    [
      authToken,
      setTransactionData,
      setIsTransactionPending,
      setIsRechargePending,
      setCanSubmit,
      setRequestId
    ]
  );

  useEffect(() => {
    setLoading(true);
    setNextButtonText('Continuar');
    setVisualStepperIndex(currentStepIndex);
    setGoBack({
      action: () => {
        setCurrentStep(step => step - 1);
      }
    });
    setCardData(stepperData.prepaidMeterCard);
    if (stepperData.prepaidMeterCard.canWriteToken) {
      setCanSubmit(true);
      getPendingRecharge(stepperData.prepaidMeterCard.meterSerial);
    } else {
      setLoading(false);
    }
  }, [
    setNextButtonText,
    setVisualStepperIndex,
    currentStepIndex,
    setGoBack,
    setCurrentStep,
    setCardData,
    stepperData,
    getPendingRecharge,
    setCanSubmit
  ]);

  const goToRecharge = useCallback(() => {
    if (isRechargePending) {
      setCurrentStep(PrepaidMeterRechargeStep.RechargeCard);
    } else {
      setCurrentStep(step => step + 1);
    }
  }, [isRechargePending, setCurrentStep]);

  const { handleSubmit } = useForm({
    submitFocusError: true,
    defaultValues: {
      ...stepperData
    }
  });

  return (
    <>
      <div className={classes.root}>
        {!loading && (isRechargePending || isTransactionPending) && (
          <div className={classes.alertsContainer}>
            {isTransactionPending ? (
              <SweetAlert
                id="PrepaidMeterRecharge_div_pendingTransaction_info"
                message={
                  <Fragment>
                    El pago de tu última recarga aún se está procesando. Vuelve
                    a intentar en unos minutos
                  </Fragment>
                }
              />
            ) : (
              <SweetAlert
                id="PrepaidMeterRecharge_div_pendingRecharge_info"
                message={
                  <Fragment>
                    Tienes una recarga pendiente de{' '}
                    {transactionData && transactionData.output.rawUnits} m3 por
                    un valor de{' '}
                    {transactionData &&
                      transactionData.output.paymentValue.toLocaleString(
                        'es-CO',
                        {
                          style: 'currency',
                          currency: 'COP',
                          minimumFractionDigits: 0
                        }
                      )}{' '}
                    pesos. Continua para pasarla a tu tarjeta.
                  </Fragment>
                }
              />
            )}
          </div>
        )}

        <div className={classes.contentContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <div className={classes.contentInner}>
              <div className="recharge-header">
                <Title
                  title="Recarga tu medidor"
                  className={classes.title}
                ></Title>
              </div>
              {cardData ? (
                <>
                  {cardData.canWriteToken ? (
                    <form
                      id={formId}
                      onSubmit={handleSubmit(goToRecharge)}
                      className={classes.infoSection}
                    >
                      <MeterInfoCard
                        id="card_data_form"
                        meterSerial={cardData.meterSerial}
                        balance={cardData.balance}
                        batteryLevel={cardData.batteryLevel}
                        time={cardData.time}
                      />
                      <Grid container className={classes.mapHintContainer}>
                        <SvgIcMapPointer
                          size={32}
                          className={classes.mapIcon}
                        />
                        <div>
                          <Typography className={classes.mapHintText}>
                            <strong>¿No sabes dónde recargar?</strong>
                          </Typography>
                          <LinkButton
                            id="PrepaidMeterRecharge_a_closer"
                            to={`${ROUTE_NAMES.map}/recharge`}
                            className={classes.mapButton}
                          >
                            Ver puntos de recarga
                            <BackIcon
                              size={16}
                              className={classes.mapButtonIcon}
                            />
                          </LinkButton>
                        </div>
                      </Grid>
                    </form>
                  ) : (
                    <Typography>
                      Tienes un saldo sin usar en tu tarjeta. Favor acercala a
                      tu medidor antes de continuar
                    </Typography>
                  )}
                </>
              ) : (
                <Typography>NO SE ENCONTRARON DATOS DE LA TARJETA</Typography>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 10px 16px'
  },
  alertsContainer: {
    width: '100%',
    maxWidth: 700,
    margin: '0 auto 10px'
  },
  contentContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  contentInner: {
    width: '100%',
    maxWidth: 700
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    textAlign: 'center'
  },
  infoSection: {
    marginTop: '20px',
    width: '100%'
  },
  mapHintContainer: {
    marginTop: '15px',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.custom.borderRadius
  },
  mapHintText: {
    fontSize: 12
  },
  mapIcon: {
    marginRight: theme.spacing(2),
    fill: theme.palette.primary.main
  },
  mapButton: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: 11
  },
  mapButtonIcon: {
    marginLeft: '5px',
    transform: 'rotate(180deg)',
    fill: theme.palette.primary.main
  }
}));

export default CardDataStep;
